/**
 * Centralized Feature Access Control
 *
 * All feature access rules based on organization type and role combinations.
 * This file consolidates access control logic for HOST_PROPERTY_MANAGEMENT and HOST_PROPERTY_DISTRIBUTION.
 *
 * Organization Types:
 * - HOST_PROPERTY_MANAGEMENT (Org Type 3)
 * - HOST_PROPERTY_DISTRIBUTION (Org Type 2)
 *
 * Roles:
 * - ROLE_SUBSCRIBER_HOST (Property Management Admin)
 * - ROLE_SUBSCRIBER_HOST_OPERATIONS (Property Management Operations)
 * - ROLE_HOST (Property Distribution Admin)
 * - ROLE_HOST_OPERATIONS (Property Distribution Operations)
 */

export const FeatureAccess = {
  /**
   * Check if user has access to Integrations page
   *
   * Rules:
   * - HOST_PROPERTY_MANAGEMENT + ROLE_SUBSCRIBER_HOST: NO
   * - HOST_PROPERTY_MANAGEMENT + ROLE_SUBSCRIBER_HOST_OPERATIONS: NO
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST: NO
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST_OPERATIONS: NO
   */
  hasIntegrationsAccess(user) {
    const orgType = user?.organization?.type?.code;
    const role = user?.organizationRole?.code || user?.role;

    // Block for all HOST_PROPERTY_MANAGEMENT roles
    if (orgType === "HOST_PROPERTY_MANAGEMENT") {
      return !["ROLE_SUBSCRIBER_HOST", "ROLE_SUBSCRIBER_HOST_OPERATIONS"].includes(role);
    }

    // Block for all HOST_PROPERTY_DISTRIBUTION roles
    if (orgType === "HOST_PROPERTY_DISTRIBUTION") {
      return !["ROLE_HOST", "ROLE_HOST_OPERATIONS"].includes(role);
    }

    return true;
  },

  /**
   * Check if user can create bookings
   *
   * Rules:
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST: NO
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST_OPERATIONS: NO
   */
  canCreateBooking(user) {
    const orgType = user?.organization?.type?.code;
    const role = user?.organizationRole?.code || user?.role;

    // Block for HOST_PROPERTY_DISTRIBUTION roles only
    if (orgType === "HOST_PROPERTY_DISTRIBUTION") {
      return !["ROLE_HOST", "ROLE_HOST_OPERATIONS"].includes(role);
    }

    return true;
  },

  /**
   * Check if user has access to Users page
   *
   * Rules:
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST_OPERATIONS: NO (entire page)
   */
  hasClientsUsersAccess(user) {
    const orgType = user?.organization?.type?.code;
    const role = user?.organizationRole?.code || user?.role;

    // Block ROLE_HOST_OPERATIONS from HOST_PROPERTY_DISTRIBUTION entirely
    if (orgType === "HOST_PROPERTY_DISTRIBUTION" && role === "ROLE_HOST_OPERATIONS") {
      return false;
    }

    // Individual customers don't need to manage other users
    if (role === "ROLE_HOST_CUSTOMER_INDIVIDUAL") {
      return false;
    }

    return true;
  },

  /**
   * Check if user can access Offers & Discounts
   *
   * Rules:
   * - Hide for HOST_PROPERTY_DISTRIBUTION org type
   */
  hasOffersDiscountsAccess(user) {
    const orgType = user?.organization?.type?.code;

    // Hide Offers & Discounts for HOST_PROPERTY_DISTRIBUTION
    if (orgType === "HOST_PROPERTY_DISTRIBUTION") {
      return false;
    }

    return true;
  },

  /**
   * Check if user can access Teams feature
   *
   * Rules:
   * - Hide for SUBSCRIBER_CUSTOMER org type
   */
  hasTeamsAccess(user) {
    const orgType = user?.organization?.type?.code;

    // Hide Teams for SUBSCRIBER_CUSTOMER
    if (orgType === "SUBSCRIBER_CUSTOMER") {
      return false;
    }

    return true;
  },

  /**
   * Check if user can add attendees to a booking
   *
   * Rules:
   * - Hide for ROLE_SUBSCRIBER_INDIVIDUAL and ROLE_HOST_CUSTOMER_INDIVIDUAL
   */
  hasAttendeeAccess(user) {
    const role = user?.organizationRole?.code || user?.role;
    const individualRoles = ["ROLE_SUBSCRIBER_INDIVIDUAL", "ROLE_HOST_CUSTOMER_INDIVIDUAL"];

    if (individualRoles.includes(role)) {
      return false;
    }

    return true;
  },

  /**
   * Check if user can manage organizations section
   *
   * Rules:
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST: NO
   * - Only HOST_PROPERTY_MANAGEMENT can see organizations
   */
  canManageOrganizations(user) {
    // Incorporate the new clients tab access logic
    if (!this.hasClientsTabAccess(user)) {
      return false;
    }

    const orgType = user?.organization?.type?.code;

    // Only HOST_PROPERTY_MANAGEMENT can manage organizations
    return orgType === "HOST_PROPERTY_MANAGEMENT";
  },

  /**
   * Check if user can add users to a specific role section
   *
   * Rules:
   * - HOST_PROPERTY_MANAGEMENT + ROLE_SUBSCRIBER_HOST_OPERATIONS:
   *   - Cannot add to Administrator accounts (ROLE_SUBSCRIBER_HOST)
   *   - Cannot add to Staff accounts (ROLE_SUBSCRIBER_HOST_OPERATIONS)
   */
  canAddUserToRole(user, targetRoleCode) {
    const role = user?.organizationRole?.code || user?.role;

    // ROLE_SUBSCRIBER_HOST_OPERATIONS cannot add to Administrator or Staff sections
    if (role === "ROLE_SUBSCRIBER_HOST_OPERATIONS") {
      const blockedRoles = ["ROLE_SUBSCRIBER_HOST", "ROLE_SUBSCRIBER_HOST_OPERATIONS"];
      return !blockedRoles.includes(targetRoleCode);
    }

    return true;
  },

  /**
   * Check if user can manage invoices
   *
   * Rules:
   * - Only HOST_PROPERTY_MANAGEMENT can manage invoices
   * - HOST_PROPERTY_DISTRIBUTION sees only transactions
   */
  canManageInvoices(user) {
    const org = user?.organization;
    const orgType = org?.type?.code || org?.typeCode || org?.code;
    return orgType === "HOST_PROPERTY_MANAGEMENT";
  },

  /**
   * Check if should show limited billing statistics
   *
   * Rules:
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST: Show only Total Revenue + Total Bookings
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST_OPERATIONS: Show only Total Revenue + Total Bookings
   */
  shouldShowLimitedStats(user) {
    const orgType = user?.organization?.type?.code;
    const role = user?.organizationRole?.code || user?.role;

    // Show limited stats for HOST_PROPERTY_DISTRIBUTION roles
    if (orgType === "HOST_PROPERTY_DISTRIBUTION") {
      return ["ROLE_HOST", "ROLE_HOST_OPERATIONS"].includes(role);
    }

    return false;
  },

  /**
   * Check if user can create announcements
   *
   * Rules:
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST: NO
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST_OPERATIONS: NO
   */
  canCreateAnnouncement(user) {
    const orgType = user?.organization?.type?.code;
    const role = user?.organizationRole?.code || user?.role;

    // Block for HOST_PROPERTY_DISTRIBUTION roles
    if (orgType === "HOST_PROPERTY_DISTRIBUTION") {
      return !["ROLE_HOST", "ROLE_HOST_OPERATIONS"].includes(role);
    }

    // Block for host individual customer accounts
    if (role === "ROLE_HOST_CUSTOMER_INDIVIDUAL") {
      return false;
    }

    return true;
  },

  /**
   * Check if user can access Announcements feature
   *
   * Rules:
   * - Hide for HOST_PROPERTY_DISTRIBUTION org type
   */
  hasAnnouncementsAccess(user) {
    const orgType = user?.organization?.type?.code;

    // Hide Announcements for HOST_PROPERTY_DISTRIBUTION
    if (orgType === "HOST_PROPERTY_DISTRIBUTION") {
      return false;
    }

    return true;
  },

  /**
   * Check if user can create offers, discounts, or packages
   *
   * Rules:
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST: NO
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST_OPERATIONS: NO
   */
  canCreateOffersDiscounts(user) {
    const orgType = user?.organization?.type?.code;
    const role = user?.organizationRole?.code || user?.role;

    // Block for HOST_PROPERTY_DISTRIBUTION roles
    if (orgType === "HOST_PROPERTY_DISTRIBUTION") {
      return !["ROLE_HOST", "ROLE_HOST_OPERATIONS"].includes(role);
    }

    return true;
  },

  /**
   * Get label for Individual Accounts section
   *
   * Rules:
   * - HOST_PROPERTY_MANAGEMENT: "Individual customer accounts"
   * - Others: "Individual accounts"
   */
  getIndividualAccountsLabel(user) {
    const orgType = user?.organization?.type?.code;
    return orgType === "HOST_PROPERTY_MANAGEMENT" ? "Individual customer accounts" : "Individual accounts";
  },

  /**
   * Filter discounts based on role
   *
   * Rules:
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST/ROLE_HOST_OPERATIONS:
   *   Show only active discounts created by Platform Admin (orgTypeId 1)
   */
  filterDiscountsForRole(discounts, user) {
    const orgType = user?.organization?.type?.code;
    const role = user?.organizationRole?.code || user?.role;

    // Filter for HOST_PROPERTY_DISTRIBUTION roles
    if (orgType === "HOST_PROPERTY_DISTRIBUTION" && ["ROLE_HOST", "ROLE_HOST_OPERATIONS"].includes(role)) {
      return discounts.filter(discount => {
        const isActive = discount.active !== false;
        // Handle both organization.typeId and organizationTypeId
        const isPlatformAdmin = discount.organization?.typeId === 1 || discount.organizationTypeId === 1;
        return isActive && isPlatformAdmin;
      });
    }

    return discounts;
  },

  /**
   * Filter offers based on role
   *
   * Rules:
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST/ROLE_HOST_OPERATIONS:
   *   Show only active offers created by Platform Admin (orgTypeId 1)
   */
  filterOffersForRole(offers, user) {
    const orgType = user?.organization?.type?.code;
    const role = user?.organizationRole?.code || user?.role;

    // Filter for HOST_PROPERTY_DISTRIBUTION roles
    if (orgType === "HOST_PROPERTY_DISTRIBUTION" && ["ROLE_HOST", "ROLE_HOST_OPERATIONS"].includes(role)) {
      return offers.filter(offer => {
        const isActive = offer.active !== false;
        // Offers use provider.typeId or providerTypeId instead of organization.typeId
        const isPlatformAdmin = offer.provider?.typeId === 1 || offer.providerTypeId === 1;
        return isActive && isPlatformAdmin;
      });
    }

    return offers;
  },

  /**
   * Filter packages based on role
   *
   * Rules:
   * - HOST_PROPERTY_DISTRIBUTION + ROLE_HOST/ROLE_HOST_OPERATIONS:
   *   Show only:
   *   (a) STANDARD packages that are active and created by Platform Admin
   *   (b) CUSTOM packages where the host is the service provider
   */
  filterPackagesForRole(packages, user) {
    const orgType = user?.organization?.type?.code;
    const role = user?.organizationRole?.code || user?.role;
    const userOrgId = user?.organizationId || user?.organization?.id;

    // Filter for HOST_PROPERTY_DISTRIBUTION roles
    if (orgType === "HOST_PROPERTY_DISTRIBUTION" && ["ROLE_HOST", "ROLE_HOST_OPERATIONS"].includes(role)) {
      return packages.filter(pkg => {
        const isActive = pkg.active !== false;
        const isStandard = pkg.type === "STANDARD";
        const isCustom = pkg.type === "CUSTOM";
        // Handle both organization.typeId and organizationTypeId
        const isPlatformAdmin = pkg.organization?.typeId === 1 || pkg.organizationTypeId === 1;
        const isHostProvider = pkg.providerId === userOrgId || pkg.provider?.id === userOrgId;

        // (a) STANDARD from Platform Admin OR (b) CUSTOM where host is provider
        return (isActive && isStandard && isPlatformAdmin) || (isCustom && isHostProvider);
      });
    }

    return packages;
  },

  /**
   * Get the appropriate redirect path after successful payment
   *
   * Rules:
   * - Host customer roles → /host/customer-dashboard
   * - Host admin/operations roles → /host/dashboard
   * - All other roles → /reservations
   */
  getPostPaymentRedirect(user) {
    const role = user?.organizationRole?.code || user?.role;

    // Host customer roles go to customer dashboard
    const customerRoles = [
      "ROLE_HOST_CUSTOMER_INDIVIDUAL",
      "ROLE_HOST_CUSTOMER_OWNER",
      "ROLE_HOST_CUSTOMER_APPROVER_EMPLOYEE",
      "ROLE_HOST_CUSTOMER_EMPLOYEE",
    ];

    if (customerRoles.includes(role)) {
      return "/host/customer-dashboard";
    }

    // Host admin/operations roles go to host dashboard
    const hostRoles = ["ROLE_HOST", "ROLE_HOST_OPERATIONS", "ROLE_SUBSCRIBER_HOST", "ROLE_SUBSCRIBER_HOST_OPERATIONS"];

    if (hostRoles.includes(role)) {
      return "/host/dashboard";
    }

    // Default to bookings page for all other roles:
    // ROLE_ADMINISTRATOR (will never purchase a subscription), ROLE_SUBSCRIBER_OWNER, ROLE_APPROVER_EMPLOYEE,
    // ROLE_EMPLOYEE, ROLE_SUBSCRIBER_INDIVIDUAL
    return "/reservations";
  },

  /**
   * Check if user is platform admin
   *
   * Rules:
   * - orgTypeId === 1 identifies the platform admin organization
   */
  isPlatformAdmin(user) {
    const org = user?.organization || {};
    // Check both potential locations for the type ID
    const orgTypeId = org.typeId || org.id || org.type?.id;
    return orgTypeId === 1;
  },

  /**
   * Check if user should see the Staff tab in Messages/Users
   *
   * Rules:
   * - Hide for ROLE_HOST_CUSTOMER_INDIVIDUAL
   */
  hasStaffTabAccess(user) {
    const role = user?.organizationRole?.code || user?.role;
    return role !== "ROLE_HOST_CUSTOMER_INDIVIDUAL";
  },

  /**
   * Check if user should see the Parent Staff tab (messaging).
   * Searches users in the parent organization rather than the current org.
   *
   * Rules:
   * - Show when user's organization has a parentId (e.g. HOST_CORPORATE_CUSTOMER, ROLE_HOST_CUSTOMER_INDIVIDUAL)
   */
  hasParentStaffTabAccess(user) {
    return user?.organization?.parentId != null;
  },

  /**
   * Check if user should see the Clients tab/Organizations section
   *
   * Rules:
   * - Hide for ROLE_HOST_CUSTOMER_OWNER
   * - Hide for HOST_PROPERTY_DISTRIBUTION org type
   * - Hide for HOST_CORPORATE_CUSTOMER org type
   */
  hasClientsTabAccess(user) {
    const orgType = user?.organization?.type?.code;
    const role = user?.organizationRole?.code || user?.role;

    if (orgType === "HOST_PROPERTY_DISTRIBUTION") {
      return false;
    }

    if (orgType === "HOST_CORPORATE_CUSTOMER") {
      return false;
    }

    if (role === "ROLE_HOST_CUSTOMER_OWNER") {
      return false;
    }

    return true;
  },

  /**
   * Check if user can edit other users (edit details, change roles, delete, toggle status)
   *
   * Rules:
   * - Operations roles cannot edit users (ROLE_HOST_OPERATIONS, ROLE_SUBSCRIBER_HOST_OPERATIONS)
   */
  canEditUsers(user) {
    const role = user?.organizationRole?.code || user?.role;
    const operationRoles = ["ROLE_HOST_OPERATIONS", "ROLE_SUBSCRIBER_HOST_OPERATIONS"];
    return !operationRoles.includes(role);
  },

  /**
   * Check if user can delete other users
   *
   * Rules:
   * - Operations roles cannot delete users (ROLE_HOST_OPERATIONS, ROLE_SUBSCRIBER_HOST_OPERATIONS)
   */
  canDeleteUsers(user) {
    const role = user?.organizationRole?.code || user?.role;
    const operationRoles = ["ROLE_HOST_OPERATIONS", "ROLE_SUBSCRIBER_HOST_OPERATIONS"];
    return !operationRoles.includes(role);
  },

  /**
   * Check if user should see the "Subscriptions & Purchase" button in sidebar
   * Currently visible to everyone, but kept as a function for future role-based restrictions
   */
  shouldShowSubscriptionAndPurchasesButton(user) {
    const role = user?.organizationRole?.code || user?.role;
    const hideRoles = [];
    return !hideRoles.includes(role);
  },

  /**
   * Check if user can upgrade their plan
   * Hidden for:
   * - ROLE_HOST_CUSTOMER_INDIVIDUAL (role under HOST_PROPERTY_MANAGEMENT org)
   * - HOST_CORPORATE_CUSTOMER org type (covers ROLE_HOST_CUSTOMER_OWNER, _APPROVER_EMPLOYEE, _EMPLOYEE)
   */
  canUpgradePlan(user) {
    const role = user?.organizationRole?.code || user?.role;
    const orgType = user?.organization?.type?.code;

    if (orgType === "HOST_CORPORATE_CUSTOMER") return false;
    if (role === "ROLE_HOST_CUSTOMER_INDIVIDUAL") return false;

    return true;
  },
};
